import { createApi } from '@reduxjs/toolkit/dist/query/react';

import serverRoutes from '@app/utils/server-routes';
import { Range } from '@app/components/ui/easy-filter/types';

import {
  MetricClassifierModelsType,
  MetricListArrType,
  MetricObjType,
  MetricTypesArrType,
  TagMetricRequest,
} from '@app/interfaces/pages-types/anatylics-metric.type';

import { metricRecordsApi } from '@app/store/api/metric-records.api';

import { baseQuery } from '../baseQuery';

export const metricsApi = createApi({
  reducerPath: 'metricsApi',
  tagTypes: ['metrics', 'singleMetric'],
  baseQuery: baseQuery,
  endpoints: (build) => ({
    // metric types
    getMetricTypesList: build.query<MetricTypesArrType[], void>({
      query: () => ({
        url: serverRoutes.metricsTypes,
      }),
    }),
    // classifierModelsList
    getClassifierModelsList: build.query<MetricClassifierModelsType[], void>({
      query: () => ({
        url: serverRoutes.metricsClassifierModels,
      }),
      transformResponse: (value: { models: MetricClassifierModelsType[] }) => value.models,
    }),
    getClassifierSecondModelsList: build.query<MetricClassifierModelsType[], void>({
      query: () => ({
        url: serverRoutes.metricsClassifierSecondModels,
      }),
      transformResponse: (value: { models: MetricClassifierModelsType[] }) => value.models,
    }),
    // project metric
    getProjectMetric: build.query<MetricObjType, { project_id: string; metric_id: string }>({
      query: (params) => ({
        url: serverRoutes.metricProject,
        params,
      }),
      providesTags: ['singleMetric'],
    }),
    getProjectMetricsList: build.query<MetricListArrType[], { project_id: string }>({
      query: (params) => ({
        url: serverRoutes.metric,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ metric_id }) => ({
                type: 'metrics' as const,
                metric_id,
              })),
              'metrics',
            ]
          : ['metrics'],
    }),
    createProjectMetricGroup: build.mutation<
      MetricObjType,
      {
        params: {
          project_id: string;
        };
        body: MetricObjType;
      }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.metricProject,
        method: 'POST',
        body,
        params,
      }),
      invalidatesTags: ['metrics'],
    }),
    editProjectMetricGroup: build.mutation<
      MetricObjType,
      {
        params: {
          project_id: string;
          metric_id: string;
        };
        body: MetricObjType;
      }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.metricProject,
        method: 'PUT',
        params,
        body,
      }),
      invalidatesTags: ['metrics', 'singleMetric'],
    }),
    deleteProjectMetric: build.mutation({
      query: (params: { project_id: string; metric_id: string }) => ({
        url: serverRoutes.projectMetric,
        method: 'DELETE',
        params,
        redirect: 'manual',
      }),

      invalidatesTags: ['metrics'],
    }),
    /* manual metric values set */
    setValueTagMetric: build.mutation<{ success: boolean }, TagMetricRequest>({
      query: ({ params, body }) => ({
        url: serverRoutes.projectMetricValue,
        method: 'PUT',
        params,
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(metricRecordsApi.util.invalidateTags(['metricRecords']));
      },
      // invalidatesTags: ['metrics'],
    }),
    //recalculate
    recalculateMetrics: build.query<
      unknown,
      {
        params: {
          id: string;
          tags_changed?: boolean;
          gpt?: boolean;
        };
        body?: {
          range?: Range;
        };
      }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.metrics_recalculate,
        params,
        body,
        method: 'PUT',
      }),
    }),

    //recalculate
    // meta metric multi
    createMetaMetricMulti: build.mutation<
      MetricObjType[],
      {
        params: {
          project_id: string;
        };
        body: MetricObjType[];
      }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.metricProjectBatch,
        method: 'POST',
        body,
        params,
      }),
      invalidatesTags: ['metrics'],
    }),
    // meta metric multi
  }),
});

export const {
  useGetMetricTypesListQuery,
  useGetProjectMetricsListQuery,
  useDeleteProjectMetricMutation,
  useLazyGetProjectMetricQuery,
  useGetProjectMetricQuery,
  useCreateProjectMetricGroupMutation,
  useEditProjectMetricGroupMutation,
  useSetValueTagMetricMutation,
  useGetClassifierModelsListQuery,
  useGetClassifierSecondModelsListQuery,
  //recalc
  useLazyRecalculateMetricsQuery,
  // meta multi
  useCreateMetaMetricMultiMutation,
} = metricsApi;
